import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ImageFileUpload, putData, postData, postFormData } from "../../../../utility/Utility";

export default function UpdateHeader() {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    logo: null,
    existingImage: "",
    status: ""
  });
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [errors, setErrors] = useState({
    email: "",
    phone: ""
  });

  useEffect(() => {
    if (state?.categoryData) {
      const { email, phone, logo, status } = state.categoryData;

      setFormData({
        email: email || "",
        phone: phone || "",
        logo: null,
        existingImage: logo || "",
        status: status || 'active'
      });

      if (logo) {
        setImagePreview(logo);
      }
    }
  }, [state]);

  const validateField = (name, value) => {
    let error = "";

    if (!value.trim()) {
      error = "This field is required";
    } else if (name === "email" && !/^\S+@\S+\.\S+$/.test(value)) {
      error = "Please enter a valid email address";
    } else if (name === "phone" && !/^[\d\s\+\-\(\)]{10,15}$/.test(value)) {
      error = "Please enter a valid phone number (10-15 digits)";
    }

    setErrors(prev => ({ ...prev, [name]: error }));
    return !error;
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "logo" && files && files[0]) {
      const file = files[0];
      setFormData((prev) => ({ ...prev, [name]: file }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      validateField(name, value);
    }
  };

  const uploadImage = async () => {
    if (!formData.logo) return formData.existingImage;

    const imageFormData = new FormData();
    imageFormData.append("file", formData.logo);

    try {
      const response = await ImageFileUpload("api/admin/upload?is_upload=s3", imageFormData);

      if (response?.success) {
        return response?.url || response.data?.imageUrl || response.data?.filePath;
      } else {
        toast.error(response?.message || "Image upload failed");
        return null;
      }
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error(error.response?.data?.message || "Error uploading image");
      return null;
    }
  };

  const validateForm = () => {
    let isValid = true;
    isValid = validateField("email", formData.email) && isValid;
    isValid = validateField("phone", formData.phone) && isValid;
    return isValid;
  };

  const toggleStatus = () => {
    setFormData((prev) => ({
      ...prev,
      status: prev.status === "active" ? "inactive" : "active"
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setLoading(true);

    try {
      let logoUrl = formData.existingImage;
      if (formData.logo) {
        const uploadedImageUrl = await uploadImage();
        if (!uploadedImageUrl) {
          setLoading(false);
          return;
        }
        logoUrl = uploadedImageUrl;
      }

      // Create FormData for the update with correct field names
      const formDataToSend = new FormData();
      formDataToSend.append('id', state?.categoryData?._id);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('status', formData.status || 'active');

      // If there's a logo, append it
      if (logoUrl) {
        formDataToSend.append('logo', logoUrl);
      }

      const response = await postData("api/admin/header-settings/update", formDataToSend);

      if (response?.success) {
        toast.success("Header updated successfully!");
        navigate(-1);
      } else {
        throw new Error(response?.message || "Failed to update header");
      }
    } catch (error) {
      console.error("Update header error:", error);
      toast.error(error.response?.data?.message || error.message || "Error updating header");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={5000} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <div className="page-email-box">
                <h4 className="mb-1">Header Management</h4>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">Header</li>
                  <li className="breadcrumb-item active">Update Header</li>
                </ol>
              </div>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                <i className="fas fa-arrow-left me-1"></i> Back
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-email mb-0">Update Header</h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    {/* Email */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="email" className="form-label fw-medium">
                          Email <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="email"
                          id="email"
                          name="email"
                          className={`form-control ${errors.email ? "is-invalid" : ""}`}
                          placeholder="Enter Email"
                          value={formData.email}
                          onChange={handleInputChange}
                          onBlur={(e) => validateField("email", e.target.value)}
                          required
                        />
                        {errors.email && (
                          <div className="invalid-feedback">{errors.email}</div>
                        )}
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="phone" className="form-label fw-medium">
                          Phone <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                          placeholder="Enter Phone Number"
                          value={formData.phone}
                          onChange={handleInputChange}
                          onBlur={(e) => validateField("phone", e.target.value)}
                          required
                        />
                        {errors.phone && (
                          <div className="invalid-feedback">{errors.phone}</div>
                        )}
                      </div>
                    </div>

                    {/* Logo Image */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="logo" className="form-label fw-medium">
                          Logo Image
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="file"
                          id="logo"
                          name="logo"
                          className="form-control"
                          onChange={handleInputChange}
                          accept="image/*"
                        />
                        {imagePreview && (
                          <div className="mt-2">
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="img-thumbnail"
                              style={{ maxWidth: "200px", maxHeight: "150px" }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">Status</label>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check form-switch d-flex align-items-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="status"
                            checked={formData.status === "active"}
                            onChange={toggleStatus}
                            role="switch"
                            style={{ width: "2.5em", height: "1.25em" }}
                          />
                          <label className="form-check-label ms-2" htmlFor="status">
                            {formData.status === "active" ? (
                              <span className="text-success">Active</span>
                            ) : (
                              <span className="text-muted">Inactive</span>
                            )}
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="row mt-4">
                      <div className="col-md-6 offset-md-3">
                        <button
                          type="submit"
                          className="btn btn-primary py-2"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Processing...
                            </>
                          ) : (
                            "Update Header"
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}